import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { DATE_INPUT_DISPLAY_FORMAT, formatDateInputDisplay, parseDateInputDisplay, } from "@/utils/dateTime";
export const DateInput = ({ value, onChange, onInvalid, placeholder, onBlur, onFocus, onClick, ...props }) => {
    const formatted = useMemo(() => formatDateInputDisplay(value), [value]);
    const [text, setText] = useState(formatted);
    const isFocusedRef = useRef(false);
    const nativeDateInputRef = useRef(null);
    const anchorRectRef = useRef(null);
    const suppressNextBlurRef = useRef(false);
    useEffect(() => {
        if (isFocusedRef.current)
            return;
        setText(formatted);
    }, [formatted]);
    const openNativePicker = () => {
        const el = nativeDateInputRef.current;
        if (!el || props.disabled)
            return;
        const rect = anchorRectRef.current;
        if (rect) {
            // Native pickers anchor to the input's on-screen box. Move the hidden date input
            // to overlap the visible text input so the calendar opens near where the user clicked.
            el.style.position = "fixed";
            el.style.top = `${Math.max(0, rect.top)}px`;
            el.style.left = `${Math.max(0, rect.left)}px`;
            el.style.width = `${Math.max(1, rect.width)}px`;
            el.style.height = `${Math.max(1, rect.height)}px`;
            // Force layout flush so the picker anchors correctly on first open.
            void el.getBoundingClientRect();
        }
        // Best-effort: try showPicker (Chromium), else click() (Safari), else focus().
        if (typeof el.showPicker === "function") {
            el.showPicker();
            return;
        }
        try {
            el.click();
        }
        catch {
            // ignore
        }
        el.focus();
    };
    return (_jsxs(_Fragment, { children: [_jsx("input", { ...props, type: "text", inputMode: "numeric", placeholder: placeholder ?? DATE_INPUT_DISPLAY_FORMAT, value: text, onClick: (e) => {
                    const target = e.currentTarget;
                    const rect = target.getBoundingClientRect();
                    anchorRectRef.current = {
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                    };
                    // Opening the native picker can steal focus, which triggers a blur on this input.
                    // That blur would parse the *old* text and overwrite the newly selected value.
                    suppressNextBlurRef.current = true;
                    openNativePicker();
                    onClick?.(e);
                }, onFocus: (e) => {
                    isFocusedRef.current = true;
                    onFocus?.(e);
                }, onChange: (e) => setText(e.target.value), onBlur: (e) => {
                    isFocusedRef.current = false;
                    if (suppressNextBlurRef.current) {
                        suppressNextBlurRef.current = false;
                        onBlur?.(e);
                        return;
                    }
                    const parsed = parseDateInputDisplay(e.target.value);
                    if (parsed.isValid) {
                        onChange(parsed.value);
                        setText(formatDateInputDisplay(parsed.value));
                    }
                    else {
                        onInvalid?.(e.target.value);
                        // Revert to last known good value.
                        setText(formatted);
                    }
                    onBlur?.(e);
                } }), _jsx("input", { ref: nativeDateInputRef, type: "date", tabIndex: -1, "aria-hidden": "true", value: value ?? "", onChange: (e) => {
                    // Native picker gives YYYY-MM-DD; keep stored value unchanged (API expectation),
                    // and let the visible input format it as DD-MMM-YYYY.
                    suppressNextBlurRef.current = false;
                    onChange(e.target.value);
                    setText(formatDateInputDisplay(e.target.value));
                }, style: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: 1,
                    height: 1,
                    opacity: 0,
                    // allow programmatic click/focus; prevent mouse interactions
                    pointerEvents: "none",
                } })] }));
};
